import { Alert, Box, CircularProgress, Paper, TextField, Typography } from "@mui/material";
import parse from "html-react-parser";
import SendIcon from "@mui/icons-material/Send";
import MDButton from "components/MDButton";

export default function AiChatDialogContent({
  slideSubject = "histology",
  aiError,
  captureLoading,
  capturedImage,
  aiMessages,
  aiQuestion,
  setAiQuestion,
  onSend,
  aiLoading,
  captureLoadingForDisable,
  isMobile,
  chatMessagesRef,
  hasDrawing,
  onClearDrawing,
  getContextTitle,
  setupDrawingCanvas,
  baseImageRef,
  drawCanvasRef,
  onDrawStart,
  onDrawMove,
  onDrawEnd,
  aiCaptureViewportRef,
}) {
  const subjectLabel =
    slideSubject === "embryology"
      ? "Embryology"
      : slideSubject === "pathology"
        ? "Pathology"
        : "Histology";

  return (
    <>
      {aiError ? <Alert severity="warning" sx={{ mb: 2 }}>{aiError}</Alert> : null}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Draw on the slide, then ask your question. Our {subjectLabel.toLowerCase()} tutor
        analyzes the marked image with specialized microscopy expertise.
      </Typography>

      <Box
        ref={aiCaptureViewportRef}
        sx={{
          position: "relative",
          width: "100%",
          borderRadius: 1,
          overflow: "hidden",
          border: "1px solid #ddd",
          mb: 1.5,
          minHeight: 200,
          touchAction: "none",
          background: "#f7f7f7",
        }}
      >
        {captureLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 220 }}>
            <CircularProgress size={26} />
          </Box>
        ) : capturedImage ? (
          <>
            <img
              ref={baseImageRef}
              src={capturedImage}
              alt="Slide snapshot"
              style={{ width: "100%", display: "block" }}
              onLoad={setupDrawingCanvas}
            />
            <canvas
              ref={drawCanvasRef}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                cursor: "crosshair",
              }}
              onPointerDown={onDrawStart}
              onPointerMove={onDrawMove}
              onPointerUp={onDrawEnd}
              onPointerLeave={onDrawEnd}
            />
          </>
        ) : null}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
        <MDButton color="secondary" variant="outlined" onClick={onClearDrawing} disabled={!hasDrawing}>
          Clear drawing
        </MDButton>
        <Typography variant="caption" color="text.secondary">
          Context: {getContextTitle()}
        </Typography>
      </Box>

      <Paper
        variant="outlined"
        ref={chatMessagesRef}
        sx={{
          p: 1.5,
          maxHeight: isMobile ? "32vh" : 260,
          overflowY: "auto",
          backgroundColor: "#fafafa",
          mb: 1.5,
        }}
      >
        {aiMessages.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Start a conversation about this slide.
          </Typography>
        ) : (
          aiMessages.map((msg, index) => (
            <Box
              key={`${msg.sender}-${index}`}
              sx={{
                display: "flex",
                justifyContent: msg.sender === "user" ? "flex-end" : "flex-start",
                mb: 1,
              }}
            >
              <Box
                sx={{
                  px: msg.sender === "ai" ? 0 : 1.2,
                  py: msg.sender === "ai" ? 0 : 0.9,
                  padding: msg.sender === "ai" ? "8px" : undefined,
                  borderRadius: 1.2,
                  maxWidth: "85%",
                  bgcolor: msg.sender === "user" ? "#1976d2" : "#e9ecef",
                  color: msg.sender === "user" ? "#fff" : "#222",
                  fontSize: 14,
                  whiteSpace: "pre-wrap",
                }}
              >
                {msg.sender === "ai" ? parse(msg.content || "") : msg.content}
              </Box>
            </Box>
          ))
        )}
      </Paper>

      <Box sx={{ display: "flex", gap: 1 }}>
        <TextField
          fullWidth
          placeholder={`Ask a ${subjectLabel.toLowerCase()} question about this slide…`}
          value={aiQuestion}
          onChange={(e) => setAiQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              onSend();
            }
          }}
          multiline
          minRows={1}
          maxRows={4}
        />
        <MDButton
          color="info"
          variant="contained"
          onClick={onSend}
          disabled={aiLoading || !aiQuestion.trim() || captureLoadingForDisable}
        >
          {aiLoading ? <CircularProgress size={18} color="inherit" /> : <SendIcon fontSize="small" />}
        </MDButton>
      </Box>
    </>
  );
}

